import { useMemo, useState, useCallback, useEffect, useRef } from "react";
import type {
  Organization,
  ProviderFilters,
  GeoPoint,
} from "../providers.types";
import { calculateDistance, formatDistanceForCountry } from "../utils/distance";
import {
  areOrganizationDatasetsLoaded,
  getDatasetKeysForCountries,
  getLoadedOrganizationsForDatasetKeys,
  loadOrganizationDatasets,
  ORGANIZATION_COUNTRY_OPTIONS,
  getOrganizationCountryLabel,
  type OrganizationCountryOption,
} from "../data/organizationData";

const PAGE_SIZE = 20;

export {
  ORGANIZATION_COUNTRY_OPTIONS,
  getOrganizationCountryLabel,
  type OrganizationCountryOption,
};

export type SortMode =
  "best-match" | "nearest" | "success-rate" | "most-experience" | "name";

export type ClinicDecisionContext = {
  userLocation?: GeoPoint;
  userInsuranceCarrier?: string;
  desiredTreatments?: string[];
  ageBand?: string | null;
};

export type ClinicDecisionSupport = {
  score: number;
  matchPercent: number;
  reasons: string[];
  matchedTreatments: string[];
  primaryReason: string;
  outcomeLabel: string;
  pricingLabel: string;
  insuranceLabel: string;
  actionLabel: string;
};

/** Normalize for fuzzy carrier/benefit matching: lowercase, strip spaces.
 * Lets a selected "Kaiser" match "Kaiser Permanente", and "Blue Cross Blue
 * Shield" match its many regional variants. */
function normalizeForMatch(value: string): string {
  return value.toLowerCase().replace(/\s+/g, "");
}

/** True if any of the org's values contains any of the selected values. */
function anyValueMatches(orgValues: string[], selected: string[]): boolean {
  const orgNorm = orgValues.map(normalizeForMatch);
  return selected.some((sel) => {
    const s = normalizeForMatch(sel);
    return orgNorm.some((o) => o.includes(s));
  });
}

function treatmentNameMatches(orgTreatment: string, desiredTreatment: string) {
  const orgValue = normalizeForMatch(orgTreatment);
  const desiredValue = normalizeForMatch(desiredTreatment);
  return orgValue === desiredValue || orgValue.includes(desiredValue);
}

function getMatchedTreatments(
  org: Organization,
  desiredTreatments: string[] = [],
) {
  if (desiredTreatments.length === 0) return [];
  const orgTreatments =
    org.treatments?.map((treatment) => treatment.name) ?? [];
  return desiredTreatments.filter((desired) =>
    orgTreatments.some((treatment) => treatmentNameMatches(treatment, desired)),
  );
}

function hasCoreTreatmentData(org: Organization) {
  return (org.treatments?.length ?? 0) > 0;
}

function getDistanceMiles(org: Organization, userLocation?: GeoPoint) {
  if (!userLocation || !org.location) return null;
  return calculateDistance(
    userLocation.lat,
    userLocation.lng,
    org.location.lat,
    org.location.lng,
  );
}

function getPreferredSuccessRate(org: Organization, ageBand?: string | null) {
  const rates = org.outcomes?.successRates ?? [];
  if (rates.length === 0) return null;

  const ageMatched = ageBand
    ? rates.find((rate) => rate.ageBand === ageBand && rate.rate > 0)
    : undefined;
  if (ageMatched) return { ...ageMatched, isAgeMatched: true };

  const under35 = rates.find((rate) => rate.ageBand === "<35" && rate.rate > 0);
  if (under35) return { ...under35, isAgeMatched: false };

  const allAges = rates.find(
    (rate) => rate.ageBand === "all-ages" && rate.rate > 0,
  );
  if (allAges) return { ...allAges, isAgeMatched: false };

  const firstPositive = rates.find((rate) => rate.rate > 0);
  return firstPositive ? { ...firstPositive, isAgeMatched: false } : null;
}

function hasPublishedSuccessRates(org: Organization) {
  return getPreferredSuccessRate(org) !== null;
}

function getListedPricingStatus(org: Organization) {
  const hasPackages = (org.pricing?.packages?.length ?? 0) > 0;
  const hasTreatmentPrice = org.treatments?.some(
    (treatment) => !!treatment.priceRange,
  );
  return hasPackages || hasTreatmentPrice;
}

function getActionLabel(org: Organization) {
  if (org.bookingUrl) return "Book online";
  if (org.phone) return "Call clinic";
  if (org.email) return "Email clinic";
  if (org.website) return "Website listed";
  return "Contact unknown";
}

function matchesFilters(org: Organization, filters: ProviderFilters): boolean {
  // Text search: name, city, state
  if (filters.search) {
    const q = filters.search.toLowerCase();
    const searchable = [
      org.name,
      org.address.city,
      org.address.state,
      ...(org.searchableSpecialties ?? []),
    ]
      .join(" ")
      .toLowerCase();
    if (!searchable.includes(q)) return false;
  }

  // Organization type
  if (filters.type && filters.type.length > 0) {
    if (!filters.type.includes(org.type)) return false;
  }

  // Practice setting (boutique / hospital-based / network group)
  if (filters.settings && filters.settings.length > 0) {
    if (!org.setting || !filters.settings.includes(org.setting)) return false;
  }

  // Distance
  if (filters.userLocation && filters.maxDistance && org.location) {
    const dist = calculateDistance(
      filters.userLocation.lat,
      filters.userLocation.lng,
      org.location.lat,
      org.location.lng,
    );
    if (dist > filters.maxDistance) return false;
  }

  // Treatments
  if (filters.treatments && filters.treatments.length > 0) {
    const orgTreatments = (org.treatments ?? []).map((t) => t.name);
    if (
      !filters.treatments.some((desired) =>
        orgTreatments.some((treatment) =>
          treatmentNameMatches(treatment, desired),
        ),
      )
    ) {
      return false;
    }
  }

  // Specialties — check both org.specialties and searchableSpecialties
  if (filters.specialties && filters.specialties.length > 0) {
    const specs = [
      ...(org.specialties ?? []),
      ...(org.searchableSpecialties ?? []),
    ];
    if (!filters.specialties.some((s) => specs.includes(s))) {
      return false;
    }
  }

  // Consultation types
  if (filters.consultationTypes && filters.consultationTypes.length > 0) {
    const orgCt = org.consultationTypes ?? [];
    if (!filters.consultationTypes.some((ct) => orgCt.includes(ct))) {
      return false;
    }
  }

  // Insurance accepted — never exclude clinics with unknown insurance status.
  // When filtering by insurance, exclude only clinics that explicitly don't accept it.
  if (filters.insuranceAccepted) {
    const accepted = org.insurance?.accepted;
    // Only exclude if explicitly set to false (not undefined/missing)
    if (accepted === false) return false;
  }

  // Insurance carriers — match any selected carrier against the clinic's list
  if (filters.carriers && filters.carriers.length > 0) {
    const orgCarriers =
      org.insurance?.carriers ?? org.insurance?.providers ?? [];
    if (!anyValueMatches(orgCarriers, filters.carriers)) return false;
  }

  // Employee / fertility benefits (Progyny, Carrot, Maven, ...)
  if (filters.employeeBenefits && filters.employeeBenefits.length > 0) {
    const orgBenefits = org.insurance?.employeeBenefits ?? [];
    if (!anyValueMatches(orgBenefits, filters.employeeBenefits)) return false;
  }

  // Languages
  if (filters.languages && filters.languages.length > 0) {
    const orgLangs = org.languages ?? [];
    if (!filters.languages.some((l) => orgLangs.includes(l))) return false;
  }

  // Country
  if (filters.country && filters.country.length > 0) {
    if (!filters.country.includes(org.address.country)) return false;
  }

  // Patient groups
  if (filters.patientGroups && filters.patientGroups.length > 0) {
    const orgGroups = org.patientGroups ?? [];
    if (!filters.patientGroups.some((g) => orgGroups.includes(g))) return false;
  }

  // Has reported outcomes
  if (filters.hasReportedOutcomes) {
    if (!hasPublishedSuccessRates(org)) return false;
  }

  return true;
}

/**
 * Get a comparable success rate for sorting. CDC clinics report per-age
 * (we use <35 as the primary), ABM clinics report a single cumulative
 * rate across all ages — both are usable as a sort key.
 */
function getSuccessRate(org: Organization): number {
  const rates = org.outcomes?.successRates;
  if (!rates || rates.length === 0) return 0;
  const under35 = rates.find((r) => r.ageBand === "<35");
  if (under35) return under35.rate;
  const allAges = rates.find((r) => r.ageBand === "all-ages");
  if (allAges) return allAges.rate;
  return rates[0]?.rate ?? 0;
}

/** Cycles/year is a good proxy for clinic size & experience. */
function getCycles(org: Organization): number {
  return org.outcomes?.cyclesPerYear ?? 0;
}

/**
 * Insurance match status for a clinic given the user's carrier.
 * - "confirmed": clinic's carriers list includes the user's carrier
 * - "accepted": clinic accepts insurance but we don't know the specific carriers
 * - "unknown": no insurance data at all
 * - "no": clinic explicitly doesn't accept insurance
 */
export type InsuranceMatchStatus = "confirmed" | "accepted" | "unknown" | "no";

export function getInsuranceMatch(
  org: Organization,
  userCarrier?: string,
): InsuranceMatchStatus {
  if (!org.insurance) return "unknown";
  if (org.insurance.accepted === false) return "no";

  if (userCarrier) {
    const carriers = org.insurance.carriers ?? [];
    const q = userCarrier.toLowerCase();
    const match = carriers.some((c: string) => c.toLowerCase().includes(q));
    if (match) return "confirmed";
    // Has carriers list but user's carrier not in it — still "accepted"
    // because the list may be incomplete
  }

  if (org.insurance.accepted) return "accepted";
  return "unknown";
}

export function getClinicDecisionSupport(
  org: Organization,
  context: ClinicDecisionContext = {},
): ClinicDecisionSupport {
  const matchedTreatments = getMatchedTreatments(
    org,
    context.desiredTreatments,
  );
  const hasDesiredTreatment = matchedTreatments.length > 0;
  const hasDesiredContext = (context.desiredTreatments?.length ?? 0) > 0;
  const successRate = getPreferredSuccessRate(org, context.ageBand);
  const distanceMiles = getDistanceMiles(org, context.userLocation);
  const insuranceMatch = getInsuranceMatch(org, context.userInsuranceCarrier);
  const hasPricing = getListedPricingStatus(org);
  const hasTelehealth = org.consultationTypes?.includes("telehealth") ?? false;
  const hasOutcomes = hasPublishedSuccessRates(org);

  let score = 0;
  let possibleScore = 0;
  let positiveScore = 0;
  if (hasDesiredContext) {
    possibleScore += 34;
    score += hasDesiredTreatment ? 34 : -10;
    positiveScore += hasDesiredTreatment ? 34 : 0;
  } else if (hasCoreTreatmentData(org)) {
    possibleScore += 8;
    score += 8;
    positiveScore += 8;
  } else {
    possibleScore += 8;
  }

  possibleScore += context.userInsuranceCarrier ? 22 : 8;
  switch (insuranceMatch) {
    case "confirmed":
      score += 22;
      positiveScore += context.userInsuranceCarrier ? 22 : 8;
      break;
    case "accepted":
      score += context.userInsuranceCarrier ? 12 : 8;
      positiveScore += context.userInsuranceCarrier ? 12 : 8;
      break;
    case "unknown":
      score += 2;
      positiveScore += 2;
      break;
    case "no":
      score -= 4;
      break;
  }

  if (distanceMiles != null) {
    possibleScore += 14;
    if (distanceMiles <= 10) {
      score += 14;
      positiveScore += 14;
    } else if (distanceMiles <= 25) {
      score += 10;
      positiveScore += 10;
    } else if (distanceMiles <= 50) {
      score += 6;
      positiveScore += 6;
    } else if (distanceMiles <= 100) {
      score += 2;
      positiveScore += 2;
    }
  }

  possibleScore += 20;
  if (successRate) {
    const outcomeScore =
      successRate.rate >= 60 ? 16 : successRate.rate >= 40 ? 11 : 7;
    score += outcomeScore;
    positiveScore += outcomeScore;
    if (successRate.isAgeMatched) {
      score += 4;
      positiveScore += 4;
    }
  } else if (hasOutcomes) {
    score += 4;
    positiveScore += 4;
  }

  const cycles = org.outcomes?.cyclesPerYear ?? 0;
  possibleScore += 6;
  if (cycles >= 500) {
    score += 6;
    positiveScore += 6;
  } else if (cycles >= 200) {
    score += 4;
    positiveScore += 4;
  } else if (cycles > 0) {
    score += 2;
    positiveScore += 2;
  }

  possibleScore += 8;
  if (hasPricing) {
    score += 8;
    positiveScore += 8;
  }
  possibleScore += 5;
  if (org.bookingUrl) {
    score += 5;
    positiveScore += 5;
  } else if (org.phone || org.email || org.website) {
    score += 2;
    positiveScore += 2;
  }
  possibleScore += 4;
  if (hasTelehealth) {
    score += 4;
    positiveScore += 4;
  }
  possibleScore += 4;
  if (org.type === "fertility-clinic") {
    score += 4;
    positiveScore += 4;
  } else if (
    org.type === "medical-practice" ||
    org.type === "solo-practitioner"
  ) {
    score += 1;
    positiveScore += 1;
  }

  const reasons: string[] = [];
  if (hasDesiredTreatment) {
    reasons.push(`Offers ${matchedTreatments[0]}`);
  }
  if (insuranceMatch === "confirmed") {
    reasons.push(
      context.userInsuranceCarrier
        ? `Takes ${context.userInsuranceCarrier}`
        : "Insurance match",
    );
  } else if (insuranceMatch === "accepted") {
    reasons.push("Accepts insurance");
  }
  if (distanceMiles != null && distanceMiles <= 100) {
    reasons.push(
      `${formatDistanceForCountry(distanceMiles, org.address.country)} away`,
    );
  }
  if (successRate) {
    reasons.push(
      successRate.isAgeMatched
        ? "Published outcomes for your age"
        : "Published outcomes",
    );
  }
  if (hasPricing) reasons.push("Pricing listed");
  if (hasTelehealth) reasons.push("Telehealth available");
  if (org.bookingUrl) reasons.push("Online booking");

  const outcomeLabel = successRate
    ? successRate.isAgeMatched
      ? `${Math.round(successRate.rate)}% for your age`
      : `${Math.round(successRate.rate)}% ${successRate.ageBand}`
    : hasOutcomes
      ? "Outcomes published"
      : "Outcomes not listed";

  const pricingLabel = hasPricing ? "Price listed" : "Pricing unknown";
  const insuranceLabel =
    insuranceMatch === "confirmed"
      ? "Insurance match"
      : insuranceMatch === "accepted"
        ? "Accepts insurance"
        : insuranceMatch === "no"
          ? "No insurance"
          : "Insurance unknown";
  const actionLabel = getActionLabel(org);

  return {
    score,
    matchPercent:
      possibleScore > 0
        ? Math.max(
            0,
            Math.min(100, Math.round((positiveScore / possibleScore) * 100)),
          )
        : 0,
    reasons,
    matchedTreatments,
    primaryReason:
      reasons[0] ??
      (hasCoreTreatmentData(org) ? "Treatments listed" : "Details in progress"),
    outcomeLabel,
    pricingLabel,
    insuranceLabel,
    actionLabel,
  };
}

/** Score for insurance-aware sorting: confirmed > accepted > unknown > no */
function insuranceScore(org: Organization, userCarrier?: string): number {
  const status = getInsuranceMatch(org, userCarrier);
  switch (status) {
    case "confirmed":
      return 3;
    case "accepted":
      return 2;
    case "unknown":
      return 1;
    case "no":
      return 0;
  }
}

function sortOrganizations(
  orgs: Organization[],
  mode: SortMode,
  decisionContext: ClinicDecisionContext = {},
): Organization[] {
  const sorted = [...orgs];
  const userLocation = decisionContext.userLocation;
  const userCarrier = decisionContext.userInsuranceCarrier;

  // Primary sort by mode, then boost by insurance match if carrier is set
  const withInsuranceBoost = (
    compareFn: (a: Organization, b: Organization) => number,
  ) => {
    if (!userCarrier) return compareFn;
    return (a: Organization, b: Organization) => {
      const insA = insuranceScore(a, userCarrier);
      const insB = insuranceScore(b, userCarrier);
      // Only separate confirmed from the rest; don't reorder within tiers
      if (insA === 3 && insB < 3) return -1;
      if (insB === 3 && insA < 3) return 1;
      return compareFn(a, b);
    };
  };

  switch (mode) {
    case "best-match":
      return sorted.sort((a, b) => {
        const scoreDiff =
          getClinicDecisionSupport(b, decisionContext).score -
          getClinicDecisionSupport(a, decisionContext).score;
        if (scoreDiff !== 0) return scoreDiff;

        const distA = getDistanceMiles(a, userLocation);
        const distB = getDistanceMiles(b, userLocation);
        if (distA != null && distB != null && distA !== distB) {
          return distA - distB;
        }
        if (distA != null && distB == null) return -1;
        if (distB != null && distA == null) return 1;

        const successDiff = getSuccessRate(b) - getSuccessRate(a);
        if (successDiff !== 0) return successDiff;

        const cyclesDiff = getCycles(b) - getCycles(a);
        if (cyclesDiff !== 0) return cyclesDiff;

        return a.name.localeCompare(b.name);
      });

    case "nearest":
      if (!userLocation) {
        return sorted.sort(
          withInsuranceBoost((a, b) => getCycles(b) - getCycles(a)),
        );
      }
      return sorted.sort(
        withInsuranceBoost((a, b) => {
          if (!a.location && !b.location) return 0;
          if (!a.location) return 1;
          if (!b.location) return -1;
          const distA = calculateDistance(
            userLocation.lat,
            userLocation.lng,
            a.location.lat,
            a.location.lng,
          );
          const distB = calculateDistance(
            userLocation.lat,
            userLocation.lng,
            b.location.lat,
            b.location.lng,
          );
          return distA - distB;
        }),
      );

    case "success-rate":
      return sorted.sort(
        withInsuranceBoost((a, b) => getSuccessRate(b) - getSuccessRate(a)),
      );

    case "most-experience":
      return sorted.sort(
        withInsuranceBoost((a, b) => getCycles(b) - getCycles(a)),
      );

    case "name":
      return sorted.sort(
        withInsuranceBoost((a, b) => a.name.localeCompare(b.name)),
      );

    default:
      return sorted;
  }
}

export function useOrganizations(
  filters: ProviderFilters,
  sortMode: SortMode = "best-match",
  decisionContext: ClinicDecisionContext = {},
) {
  const [page, setPage] = useState(1);
  const [loadError, setLoadError] = useState<Error | null>(null);

  // Serialize filters to a stable key so useMemo recomputes on any value change
  const filterKey = JSON.stringify(filters);
  const countryKey = JSON.stringify(filters.country ?? []);
  const decisionContextKey = JSON.stringify(decisionContext);
  const datasetKeys = useMemo(
    () => getDatasetKeysForCountries(filters.country),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [countryKey],
  );
  const [organizations, setOrganizations] = useState(() =>
    getLoadedOrganizationsForDatasetKeys(datasetKeys),
  );
  // A dataset that failed to load is no longer loading: the caller shows the
  // error with Try again instead of a skeleton that never resolves.
  const isLoading = !loadError && !areOrganizationDatasetsLoaded(datasetKeys);
  const [isRetrying, setIsRetrying] = useState(false);

  useEffect(() => {
    let cancelled = false;
    setLoadError(null);
    setOrganizations(getLoadedOrganizationsForDatasetKeys(datasetKeys));
    loadOrganizationDatasets(datasetKeys)
      .then(() => {
        if (!cancelled) {
          setOrganizations(getLoadedOrganizationsForDatasetKeys(datasetKeys));
        }
      })
      .catch((error: unknown) => {
        if (!cancelled) {
          // Keep the datasets that did load.
          setOrganizations(getLoadedOrganizationsForDatasetKeys(datasetKeys));
          setLoadError(
            error instanceof Error ? error : new Error(String(error)),
          );
        }
      });

    return () => {
      cancelled = true;
    };
  }, [datasetKeys]);

  // The failure stays on screen while the retry runs, so the retry button can
  // show its progress instead of flashing back to the loading state.
  const retry = useCallback(() => {
    setIsRetrying(true);
    loadOrganizationDatasets(datasetKeys)
      .then(() => {
        setLoadError(null);
      })
      .catch((error: unknown) => {
        setLoadError(error instanceof Error ? error : new Error(String(error)));
      })
      .finally(() => {
        setOrganizations(getLoadedOrganizationsForDatasetKeys(datasetKeys));
        setIsRetrying(false);
      });
  }, [datasetKeys]);

  const filtered = useMemo(() => {
    const matched = organizations.filter((org) => matchesFilters(org, filters));
    const sortingContext: ClinicDecisionContext = {
      ...decisionContext,
      userLocation: decisionContext.userLocation ?? filters.userLocation,
      userInsuranceCarrier:
        decisionContext.userInsuranceCarrier ?? filters.userInsuranceCarrier,
    };
    return sortOrganizations(matched, sortMode, sortingContext);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filterKey, sortMode, decisionContextKey, organizations]);

  // Reset page when the actual result set changes (not just reference)
  const prevIdsRef = useRef("");
  useEffect(() => {
    // Compare by first few IDs + total count as a cheap identity check
    const sig =
      filtered.length +
      ":" +
      filtered
        .slice(0, 5)
        .map((o) => o.id)
        .join(",");
    if (prevIdsRef.current !== sig) {
      prevIdsRef.current = sig;
      setPage(1);
    }
  }, [filtered]);

  const totalFiltered = filtered.length;
  const data = useMemo(
    () => filtered.slice(0, page * PAGE_SIZE),
    [filtered, page],
  );
  const hasNextPage = data.length < totalFiltered;

  const fetchNextPage = useCallback(() => {
    if (hasNextPage) setPage((p) => p + 1);
  }, [hasNextPage]);

  return {
    data,
    totalFiltered,
    hasNextPage,
    fetchNextPage,
    isLoading,
    error: loadError,
    retry,
    isRetrying,
  };
}
